import { useTranslation } from 'react-i18next'
import Box from '@mui/material/Box'
import { ArrowUpwardRounded, ArrowDownwardRounded } from '@mui/icons-material'
import { Grid, Stack, Typography, useTheme } from '@mui/material'
import { Chart } from 'react-chartjs-2'
import { GapExtended, GapItem } from '../../shared/model/models'
import { DiversityDimenstionConfig, ModuleConfig } from '../../shared/model'
import { translatedDomainName } from '../../shared/utils'
import { useAppSelector } from '../../store/hooks'
import AnimatedProgressBar from '../common/molecules/AnimatedProgressBar'

type FsGapDescriptionProps = {
  latestGap: GapExtended
  moduleConfig?: ModuleConfig
  diversityDimenstionConfig?: DiversityDimenstionConfig
  prevGap: GapItem[]
}

const FsGapDescription = (props: FsGapDescriptionProps) => {
  const { latestGap, moduleConfig, diversityDimenstionConfig, prevGap } = props
  const { t, i18n } = useTranslation()
  const theme = useTheme()
  const { currentLanguage } = useAppSelector((state) => state.currentUser)

  const referenceGroupCount = Math.round(
    latestGap?.description.referenceGroup.descriptionDependentVariable.numberOfObservations,
  )
  const groupOfInterestCount = Math.round(
    latestGap?.description.groupOfInterest.descriptionDependentVariable.numberOfObservations,
  )
  const referenceGroupPercentage =
    (referenceGroupCount * 100) / (referenceGroupCount + groupOfInterestCount)

  const groupOfInterestPercentage =
    (groupOfInterestCount * 100) / (referenceGroupCount + groupOfInterestCount)

  const sortedGaps = [...prevGap].sort((a, b) => a.id - b.id)
  const prevIndex =
    sortedGaps.length > 1 ? sortedGaps.findIndex((g) => g.id === latestGap.id) - 1 : -1
  const curFairness = 100 - Math.abs(latestGap?.unadjusted ?? 0)
  const prevFairness =
    prevIndex !== -1 && sortedGaps && sortedGaps[prevIndex]
      ? 100 - Math.abs(sortedGaps[prevIndex].unadjusted ?? 0)
      : null

  const diff =
    Math.round((curFairness + Number.EPSILON) * 10) / 10 -
    Math.round(((prevFairness ?? 0) + Number.EPSILON) * 10) / 10

  return (
    <Box className="status_walkthrough">
      <Typography variant="h2" className="font-semibold">
        Status
      </Typography>
      <Stack className="justify-between">
        <Box className="mt-4">
          <Typography variant="h3">Fairness {t('achieved')}</Typography>
          <Typography variant="n1" className="mt-4 text-center">
            {Number(curFairness).toLocaleString(currentLanguage, {
              maximumFractionDigits: 1,
              minimumFractionDigits: 0,
            })}{' '}
            %
          </Typography>
          <Grid
            size={{ xs: 12 }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            borderRadius={2}
            mx="auto"
            my={2}
            width="60%"
            minWidth={200}
          >
            <AnimatedProgressBar
              progress={curFairness > 0 ? curFairness : 0}
              color={diversityDimenstionConfig?.fsColor?.light ?? ''}
            />
          </Grid>

          {prevFairness !== null && (
            <Stack alignItems="center">
              <Stack direction="row" alignItems="flex-start" justifyContent="center" width="75%">
                <Typography variant="body2" textAlign="center" ml={0.25} mt={-0.25}>
                  {diff > 0 ? (
                    <ArrowUpwardRounded sx={{ fontSize: 16, mb: '-4px' }} color="success" />
                  ) : diff < 0 ? (
                    <ArrowDownwardRounded sx={{ fontSize: 16, mb: '-4px' }} color="error" />
                  ) : null}
                  {diff !== 0
                    ? ` ${diff > 0 ? '+' : '-'}${Math.abs(diff).toLocaleString(currentLanguage, {
                        maximumFractionDigits: 1,
                      })} % ${t('compare.to.prev.data')}`
                    : t('no.compare.to.prev.data')}
                </Typography>
              </Stack>
            </Stack>
          )}
        </Box>
        <Grid container spacing={2}>
          <Grid size={{ sm: 12, md: 6, lg: 12 }}>
            <Box className="mt-4">
              <Typography variant="h3" className="mt-10 mb-5">
                {t(
                  `dependentCharacteristic.name.${latestGap?.dependentCharacteristic.name}` as any,
                )}
              </Typography>
              <Grid container spacing={1} mt={1} display="flex" justifyContent="flex-start">
                <Grid size={{ xs: 6 }} className="flex pl-2 pt-2">
                  <Box
                    sx={{
                      backgroundColor: diversityDimenstionConfig?.fsColor?.light ?? '',
                    }}
                    className="p-[8px] h-[8px] rounded-sm mt-[3px]"
                  />
                  <Typography className="w-fit ml-2">
                    {translatedDomainName(
                      `${latestGap?.description.groupOfInterest.groupVariable.label}`,
                      t,
                    )}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Stack className="flex-row items-end justify-center flex-wrap">
                    <Box>
                      <Typography variant="n3">
                        {`${Number(
                          latestGap?.description.groupOfInterest.descriptionDependentVariable.mean,
                        ).toLocaleString(currentLanguage, {
                          maximumFractionDigits: moduleConfig?.numberFractionDigit,
                          minimumFractionDigits: moduleConfig?.numberFractionDigit,
                        })}`}
                      </Typography>
                    </Box>
                    <Box ml={1}>
                      <Typography variant="body1">{t(`${moduleConfig?.unit}` as any)}</Typography>
                    </Box>
                  </Stack>
                </Grid>
              </Grid>
              <Grid container spacing={1} mt={2} display="flex" justifyContent="flex-start">
                <Grid size={{ xs: 6 }} className="flex pl-2 pt-2">
                  <Box
                    sx={{
                      backgroundColor: theme.palette.divider,
                    }}
                    className="p-[8px] h-[8px] rounded-sm mt-[3px]"
                  />
                  <Typography variant="body1" className="w-fit ml-2">
                    {translatedDomainName(
                      `${latestGap?.description.referenceGroup.groupVariable.label}`,
                      t,
                    )}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Stack className="flex-row items-end justify-center flex-wrap">
                    <Typography variant="n3">
                      {`${Number(
                        latestGap?.description.referenceGroup.descriptionDependentVariable.mean,
                      ).toLocaleString(currentLanguage, {
                        maximumFractionDigits: moduleConfig?.numberFractionDigit,
                        minimumFractionDigits: moduleConfig?.numberFractionDigit,
                      })}`}
                    </Typography>
                    <Typography variant="body1" ml={1}>
                      {t(`${moduleConfig?.unit}` as any)}
                    </Typography>
                  </Stack>
                </Grid>
              </Grid>
            </Box>
          </Grid>
          <Grid size={{ sm: 12, md: 6, lg: 12 }}>
            <Box className="mt-4">
              <Typography variant="h3" className="mt-10 mb-5">
                {`${t('number.employees')}: ${(
                  referenceGroupCount + groupOfInterestCount
                ).toLocaleString(currentLanguage)}`}
              </Typography>
              <Grid container spacing={1} mt={1} display="flex" justifyContent="flex-start">
                <Grid size={{ xs: 6 }} className="flex pl-2 pt-2">
                  <Box
                    sx={{
                      backgroundColor: diversityDimenstionConfig?.fsColor?.light ?? '',
                    }}
                    className="p-[8px] h-[8px] rounded-sm mt-[3px]"
                  />
                  <Typography variant="body1" className="ml-2 w-fit">
                    {translatedDomainName(
                      `${latestGap?.description.groupOfInterest.groupVariable.label}`,
                      t,
                    )}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Stack className="flex-row items-end justify-center flex-wrap">
                    <Typography variant="n3">
                      {`${groupOfInterestCount.toLocaleString(currentLanguage)}`}
                    </Typography>
                    <Typography
                      variant="body1"
                      ml={1}
                    >{`(${groupOfInterestPercentage.toLocaleString(i18n.language, {
                      maximumFractionDigits: 1,
                      minimumFractionDigits: 0,
                    })} %)`}</Typography>
                  </Stack>
                </Grid>
              </Grid>
              <Grid container spacing={1} my={2} display="flex" justifyContent="flex-start">
                <Grid size={{ xs: 6 }} className="flex pl-2 pt-2">
                  <Box
                    sx={{
                      backgroundColor: theme.palette.divider,
                    }}
                    className="p-[8px] h-[8px] rounded-sm mt-[3px]"
                  />
                  <Typography variant="body1" className="ml-2 w-fit">
                    {translatedDomainName(
                      `${latestGap?.description.referenceGroup.groupVariable.label}`,
                      t,
                    )}
                  </Typography>
                </Grid>
                <Grid size={{ xs: 6 }}>
                  <Stack className="flex-row items-end justify-center flex-wrap">
                    <Typography variant="n3">
                      {`${referenceGroupCount.toLocaleString(currentLanguage)}`}
                    </Typography>
                    <Typography ml={1}>{`(${referenceGroupPercentage.toLocaleString(i18n.language, {
                      maximumFractionDigits: 1,
                      minimumFractionDigits: 0,
                    })} %)`}</Typography>
                  </Stack>
                </Grid>
              </Grid>
              {latestGap?.adjusted !== null && (
                <Stack
                  display={{ sm: 'none', md: 'none', lg: 'flex' }}
                  justifyContent="center"
                  alignItems="center"
                  mt={-3}
                >
                  <Box width={250} height={225}>
                    <Chart
                      type="bar"
                      id={`description_percentage_goi_pie_chart_${moduleConfig?.module}`}
                      data={{
                        labels: [t('number.employees'), t('number.employees')],
                        datasets: [
                          {
                            label: translatedDomainName(
                              `${latestGap?.description.groupOfInterest.groupVariable.label}`,
                              t,
                            ),
                            backgroundColor: diversityDimenstionConfig?.fsColor?.light ?? '',
                            data: [groupOfInterestPercentage],
                          },
                          {
                            label: translatedDomainName(
                              `${latestGap?.description.referenceGroup.groupVariable.label}`,
                              t,
                            ),
                            backgroundColor: theme.palette.divider,
                            data: [referenceGroupPercentage],
                            borderRadius: 8,
                          },
                        ],
                      }}
                      options={{
                        maintainAspectRatio: false,
                        locale: i18n.language,
                        elements: { arc: { borderWidth: 0 } },
                        layout: {
                          padding: {
                            bottom: 20,
                            left: 0,
                            right: 0,
                          },
                        },
                        plugins: {
                          title: {
                            display: false,
                          },
                          legend: {
                            display: false,
                          },
                          tooltip: {
                            callbacks: {
                              label: (context) =>
                                `${context.dataset.label}: ${
                                  context.datasetIndex === 0
                                    ? groupOfInterestCount.toLocaleString(i18n.language, {
                                        maximumFractionDigits: 0,
                                        minimumFractionDigits: 0,
                                      })
                                    : referenceGroupCount.toLocaleString(i18n.language, {
                                        maximumFractionDigits: 0,
                                        minimumFractionDigits: 0,
                                      })
                                } (${Number(context.raw).toLocaleString(i18n.language, {
                                  maximumFractionDigits: 1,
                                  minimumFractionDigits: 1,
                                })}) %`,
                            },
                          },
                          datalabels: {
                            anchor: 'center',
                            align: 'right',
                            display: true,
                            offset: 64,
                            color: theme.palette.text.secondary,
                            formatter: (value) =>
                              value !== '0.0'
                                ? `${Number(value).toLocaleString(i18n.language, {
                                    maximumFractionDigits: 1,
                                    minimumFractionDigits: 1,
                                  })} %`
                                : '',
                          },
                        },
                        scales: {
                          x: {
                            stacked: true,
                            grid: {
                              display: false,
                            },
                            border: {
                              display: false,
                            },
                            ticks: {
                              display: false,
                            },
                          },
                          y: {
                            stacked: true,
                            grid: {
                              color: theme.palette.divider,
                            },
                            border: {
                              display: false,
                            },
                            min: 0,
                            max: 100,
                            ticks: {
                              autoSkip: false,
                              stepSize: 50,
                              color: theme.palette.text.primary,
                              callback: (value) => `${value} %`,
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </Stack>
              )}
            </Box>
          </Grid>
        </Grid>
      </Stack>
    </Box>
  )
}

export default FsGapDescription
